import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { holeDataCache } from './holeDataCache';

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};

export default function CachedHoleRender({ name, hole_url, assay_url, survey_url = null, dataset, holetype, visible, year = [], programType = [], holeRadius = 0.5, holeColor = 'whitesmoke', onLoadComplete, onLoadFailed }) {
  const [combinedData, setCombinedData] = useState({});
  const [filterData, setFilterData] = useState({});
  const [filteredHoleIds, setFilteredHoleIds] = useState([]);
  const [holeCount, setHoleCount] = useState(0);
  const [assayCount, setAssayCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const holesRef = useRef();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Get data from cache (loads once, then uses cache)
        const allData = await holeDataCache.getData(hole_url, assay_url, survey_url);
        
        // Filter data for specific dataset and hole type
        const filteredData = holeDataCache.getFilteredData(hole_url, assay_url, dataset, holetype, year, programType, survey_url);
        
        setCombinedData(filteredData);
        setLoading(false);
        onLoadComplete?.(name);
      } catch (error) {
        console.error('Error loading hole data:', error);
        setLoading(false);
        onLoadFailed?.(name);
      }
    };

    loadData();
  }, [hole_url, assay_url, survey_url, dataset, holetype, year, programType]);

  useEffect(() => {
    const filteredData = filteredHoleIds.length > 0
      ? Object.keys(combinedData).reduce((acc, key) => {
          if (filteredHoleIds.includes(key)) {
            acc[key] = combinedData[key];
          }
          return acc;
        }, {})
      : combinedData;

    setHoleCount(Object.keys(filteredData).length);
    let assayNumber = 0;
    Object.keys(filteredData).forEach((hole, j) => {
      const data = filteredData[hole].assays;
      assayNumber += data.length;
    });
    setAssayCount(assayNumber);
    
    setFilterData(filteredData);
  }, [combinedData, filteredHoleIds]);

  if (loading) {
    return null; // or a loading component
  }

  return (
    holeCount > 0 && visible &&
    <group visible={visible} name={name}>
      <Holes count={holeCount} holes={filterData} instRef={holesRef} holeRadius={holeRadius} holeColor={holeColor} />
      <CollarSpheres holes={filterData} />
      <CurvedHoles holes={filterData} holeColor={holeColor} />
      <SelectedHoleHighlight instRef={holesRef} />
      {assayCount > 0 && <Assays count={assayCount} holes={filterData} assayRadius={holeRadius * 2.5}/>}
    </group>
  );
}

function Holes({ count, holes, instRef, holeRadius, holeColor }) {
  const [instancesData, setInstancesData] = useState([]);
  
  useEffect(() => {
    const up = new THREE.Vector3(0, 1, 0);
    const tmpQuat = new THREE.Quaternion();
    const tmpMat = new THREE.Matrix4();
    const tmpScale = new THREE.Vector3(1, 1, 1);
    const tmpPos = new THREE.Vector3();
    const dir = new THREE.Vector3();

    const idata = [];
    let i = 0;

    Object.keys(holes).forEach((holeId) => {
      const holeData = holes[holeId];
      // Only render straight holes (2 points) as cylinders here
      // Curved holes (more than 2 points) will be rendered by CurvedHoles component
      if (holeData.points.length > 2) {
        return; // Skip curved holes
      }
      
      const p0 = holeData.points[0];
      const p1 = holeData.points[1];

      // Direction and length
      dir.set(p1.x - p0.x, p1.y - p0.y, p1.z - p0.z);
      const length = dir.length();
      if (length === 0) return;

      // Midpoint
      tmpPos.set((p0.x + p1.x) / 2, (p0.y + p1.y) / 2, (p0.z + p1.z) / 2);

      // Align cylinder Y-axis to direction
      dir.normalize();
      tmpQuat.setFromUnitVectors(up, dir);

      // Scale along Y to match length
      tmpScale.set(1, length, 1);

      // Compose matrix
      tmpMat.compose(tmpPos, tmpQuat, tmpScale);

      if (instRef.current) {
        instRef.current.setMatrixAt(i, tmpMat);
      }
      i += 1;

      idata.push(holeData.data);
    });

    if (instRef.current) {
      instRef.current.instanceMatrix.needsUpdate = true;
      instRef.current.userData = instRef.current.userData || {};
      instRef.current.userData.instancesData = idata;
    }
    setInstancesData(idata);
  }, [holes]);

  // Calculate actual count of straight holes
  const straightHoleCount = Object.keys(holes).filter(holeId => holes[holeId].points.length <= 2).length;

  return (
    straightHoleCount > 0 && (
      <instancedMesh ref={instRef} args={[null, null, straightHoleCount]} userData={{instancesData:instancesData}} name='Holes'>
        {/* Unit cylinder along Y, scaled per-instance to length */}
        <cylinderGeometry args={[holeRadius, holeRadius, 1, 8]} />
        <meshBasicMaterial color={holeColor} />
      </instancedMesh>
    )
  );
}

function CollarSpheres({ holes }) {
  const ref = useRef();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const tmpMat = new THREE.Matrix4();
    const tmpPos = new THREE.Vector3();

    let i = 0;
    Object.keys(holes).forEach((holeId) => {
      const holeData = holes[holeId];
      if (!holeData?.points?.length) return;
      const p0 = holeData.points[0];
      if (!p0) return;

      tmpPos.set(p0.x, p0.y, p0.z);
      tmpMat.makeTranslation(tmpPos.x, tmpPos.y, tmpPos.z);

      if (ref.current) {
        ref.current.setMatrixAt(i, tmpMat);
      }
      i += 1;
    });

    setCount(i);
    if (ref.current) {
      ref.current.instanceMatrix.needsUpdate = true;
    }
  }, [holes]);

  if (count <= 0) return null;

  return (
    <instancedMesh ref={ref} args={[null, null, count]} name="HoleCollars">
      <sphereGeometry args={[2, 12, 12]} />
      <meshBasicMaterial color="red" />
    </instancedMesh>
  );
}

// Render curved holes using Line component
function CurvedHoles({ holes, holeColor }) {
  return (
    <>
      {Object.keys(holes).map((holeId) => {
        const holeData = holes[holeId];
        // Only render holes with more than 2 points (curved holes)
        if (holeData.points.length <= 2) {
          return null;
        }

        // Convert points array to format expected by Line component
        const linePoints = holeData.points.map(p => [p.x, p.y, p.z]);

        return (
          <Line
            key={holeId}
            points={linePoints}
            closed={false}
            curveType="centripetal"
            userData={{ displayData: holeData.data }}
            lineWidth={2}
            color={holeColor}
            name={holeId}
          />
        );
      })}
    </>
  );
}

function SelectedHoleHighlight({ instRef }) {
  const highlightRef = useRef();
  const [matrix, setMatrix] = useState(() => new THREE.Matrix4());
  const { objectDetailsObject, clickType, instanceId } = require('../../store/clickStore.js').useClickStore.getState();

  useEffect(() => {
    const unsub = require('../../store/clickStore.js').useClickStore.subscribe((state) => {
      // Trigger re-evaluation when selection changes
      if (instRef.current && state.objectDetailsObject === instRef.current && state.instanceId !== null && state.clickType === 'info') {
        const m = new THREE.Matrix4();
        instRef.current.getMatrixAt(state.instanceId, m);
        setMatrix(m);
      } else {
        // Reset by setting identity
        const m = new THREE.Matrix4();
        setMatrix(m.identity());
      }
    });
    return () => unsub();
  }, [instRef]);

  // Blink opacity
  useEffect(() => {
    let raf;
    const animate = () => {
      if (highlightRef.current && instRef.current) {
        const state = require('../../store/clickStore.js').useClickStore.getState();
        const active = state.clickType === 'info' && state.objectDetailsObject === instRef.current && state.instanceId !== null;
        if (active) {
          const t = Date.now() * 0.001;
          const opacity = 0.5 + 0.4 * Math.sin(t * 4);
          highlightRef.current.material.opacity = opacity;
        }
        highlightRef.current.visible = active;
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [instRef]);

  // Do not render until we have an instanced ref
  if (!instRef.current) return null;

  return (
    <mesh ref={highlightRef} matrixAutoUpdate={false} matrix={matrix} name='HolesHighlight'>
      <cylinderGeometry args={[0.5, 0.5, 1.02, 12]} />
      <meshBasicMaterial color={'#FFD700'} transparent opacity={0.8} depthTest={false} />
    </mesh>
  );
}

function Assays({ count, holes, assayRadius = 1.25 }) {
  const ref = useRef();
  const [colors, setColors] = useState(new Float32Array());
  const [instancesData, setInstancesData] = useState([]);

  useEffect(() => {
    let colorsArray = [];
    let idata = [];
    let i = 0;
    Object.keys(holes).forEach((hole, j) => {
      const data = holes[hole].assays;
      if (data.length > 0) {
        data.forEach((assay) => {
          const matrix = assay.matrix;
          const color = assay.color;
          const data = assay.data;
          idata.push({
            holeID: data[assayColumns.HoleID],
            from: data[assayColumns.from] + ' m',
            to: data[assayColumns.to] + ' m',
            grade: (data[assayColumns.grade] !== undefined && data[assayColumns.grade] !== null && String(data[assayColumns.grade]).trim() !== '' && Number.isFinite(parseFloat(data[assayColumns.grade])))
              ? (data[assayColumns.grade] + ' g/t')
              : 'pending'
          });
          ref.current.setMatrixAt(i, matrix);
          colorsArray.push(color.r, color.g, color.b);
          i++;
        });
      }
    });

    setInstancesData(idata);
    setColors(Float32Array.from(colorsArray));
    ref.current.instanceMatrix.needsUpdate = true;
  }, [holes]);

  return (
    <instancedMesh ref={ref} args={[null, null, count]} userData={{instancesData:instancesData}} name='Assays'>
      <cylinderGeometry args={[assayRadius, assayRadius, 1, 8]} >
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </cylinderGeometry>
      <meshLambertMaterial vertexColors toneMapped={false} />
    </instancedMesh>
  );
}
